import { Utils } from '../utils/utils.js';
import { logger } from '../utils/logger.js';
import { WidgetMessaging } from './messaging.js';
import { WIDGET_STYLES } from '../styles/index.js';
import { ClientPlanDetector } from '../utils/client-plan-detector.js';
import { WidgetAPI } from '../utils/api.js';
import { VirtualKeyboardHandler } from '../utils/virtual-keyboard-handler.js';

// Version du widget - mise à jour automatique via npm version
const WIDGET_VERSION = '2.0.0';

/**
 * Classe principale du widget Ringado
 */
export class RingadoChatWidget {
  constructor(userConfig = {}) {
    this.config = Utils.mergeConfig(userConfig);

    // Debug log the final configuration
    logger.info('RingadoChatWidget: Final merged configuration', {
      config: this.config,
      userConfig,
      scriptConfig: Utils.getScriptConfig(),
      windowConfig: window.RingadoConfig || {},
    });

    // Correction/détection intelligente du baseUrl - toujours redétecter
    const detectedBaseUrl = this.detectBaseUrl();
    this.config.baseUrl = detectedBaseUrl;

    logger.info('BaseUrl final configuration', {
      finalBaseUrl: this.config.baseUrl,
      detectedBaseUrl: detectedBaseUrl,
      originalConfigBaseUrl: userConfig.baseUrl,
      windowConfigBaseUrl: (window.RingadoConfig || {}).baseUrl,
      hostname: window.location.hostname,
      port: window.location.port,
      origin: window.location.origin,
    });

    this.state = {
      isOpen: false,
      isWidgetVisible: false,
      conversationStep: 0, // 0: name, 1: email, 2: chat
      currentConversationId: null,
      visitorName: null,
      visitorEmail: null,
      isEmailCollected: false,
      isConnectedToOperator: false,
      isOperatorViewing: false,
      isConnectingToOperator: false,
      operatorInfo: null,
      messages: [],
      isTyping: false,
      lastMessageId: null,
      // Nouveaux états pour la détection du statut Pro
      detectedProStatus: null, // null, true, false
      detectedClientName: null,
      isLoadingClientInfo: false,
    };

    this.elements = {};
    this.intervals = [];
    this.timeouts = [];

    // Initialiser le détecteur de plan client
    this.clientPlanDetector = new ClientPlanDetector(this.config.baseUrl);

    // Initialiser l'API du widget
    this.api = new WidgetAPI(this.config.baseUrl);

    // Gestionnaire du clavier virtuel (sera initialisé après la création du widget)
    this.virtualKeyboardHandler = null;

    this.init();

    logger.info('Ringado Chat Widget initialized', {
      version: this.version,
      config: this.config,
      hasUserId: !!this.config.userId,
      baseUrl: this.config.baseUrl,
      baseUrlType: typeof this.config.baseUrl,
    });
  }

  async init() {
    // Validate configuration
    this.validateConfiguration();

    // Inject styles
    this.injectStyles();

    // Détecter le statut Pro si userId est fourni
    await this.detectClientInfoIfNeeded();

    // Create widget elements
    this.createWidget();

    // Initialize responsive handling
    this.initResponsiveHandling();

    // Initialize virtual keyboard handler pour mobile
    if (this.isMobileDevice()) {
      this.virtualKeyboardHandler = new VirtualKeyboardHandler(this);
    }

    // Initialize widget messaging if in iframe
    WidgetMessaging.initWidgetCommunication();

    // Show widget with delay for transition
    if (this.config.initiallyVisible) {
      setTimeout(() => {
        this.showWidget();
      }, 100);
    }

    // Auto open if configured
    if (this.config.autoOpen) {
      setTimeout(() => {
        this.openChat();
      }, 1000);
    }

    // Call ready callback
    if (this.config.callbacks.onReady) {
      this.config.callbacks.onReady(this);
    }
  }

  injectStyles() {
    if (document.getElementById('ringado-widget-styles')) return;

    const styleElement = document.createElement('style');
    styleElement.id = 'ringado-widget-styles';
    styleElement.textContent = WIDGET_STYLES;
    document.head.appendChild(styleElement);
  }

  /**
   * Initialise la gestion responsive du widget
   */
  initResponsiveHandling() {
    // Débounce function pour éviter trop d'événements
    let resizeTimeout;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.adjustWidgetForDevice();
      }, 150);
    };

    // Écouter les changements de taille de fenêtre
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', () => {
      // Délai plus long pour l'orientation car les dimensions mettent du temps à se mettre à jour
      setTimeout(() => {
        this.adjustWidgetForDevice();
      }, 500);
    });

    // Ajustement initial
    this.adjustWidgetForDevice();

    // Stocker la fonction pour pouvoir la supprimer lors du cleanup
    this.resizeHandler = handleResize;
  }

  /**
   * Ajuste dynamiquement le widget selon les dimensions de l'appareil
   */
  adjustWidgetForDevice() {
    if (!this.elements.modal) return;

    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
      isMobile: window.innerWidth <= 768,
      isSmallMobile: window.innerWidth <= 480,
      isShortScreen: window.innerHeight <= 600,
      isVeryShortScreen: window.innerHeight <= 500,
      isLandscape: window.innerWidth > window.innerHeight,
    };

    // Détecter des appareils spécifiques
    const deviceInfo = this.detectSpecificDevice(viewport);

    // Calculer la hauteur optimale selon l'appareil
    let optimalHeight;

    if (deviceInfo.isIPhoneLandscape) {
      // iPhone en mode paysage - très optimisé
      optimalHeight = Math.min(viewport.height - 20, 250);
    } else if (viewport.isVeryShortScreen) {
      // Écrans très courts (ex: iPhone en paysage)
      optimalHeight = Math.min(viewport.height - 40, 300);
    } else if (viewport.isShortScreen) {
      // Écrans courts
      optimalHeight = Math.min(viewport.height - 60, 350);
    } else if (viewport.isSmallMobile) {
      // Petits mobiles
      optimalHeight = Math.min(viewport.height - 100, 450);
    } else if (viewport.isMobile) {
      // Tablettes et grands mobiles
      optimalHeight = Math.min(viewport.height - 140, 550);
    } else {
      // Desktop - hauteur par défaut
      optimalHeight = 600;
    }

    // Ajustements supplémentaires pour certains appareils
    if (deviceInfo.isIOS && viewport.isSmallMobile) {
      // Sur iOS mobile, laisser plus d'espace pour la barre d'adresse
      optimalHeight = Math.min(optimalHeight, viewport.height - 120);
    }

    // Appliquer la hauteur via une classe CSS dynamique
    const className = `ringado-dynamic-height-${Math.round(optimalHeight)}`;

    // Supprimer les anciennes classes de hauteur dynamique
    this.elements.modal.classList.forEach((cls) => {
      if (cls.startsWith('ringado-dynamic-height-')) {
        this.elements.modal.classList.remove(cls);
      }
    });

    // Ajouter la nouvelle classe
    this.elements.modal.classList.add(className);

    // Injecter le style CSS correspondant si pas déjà fait
    if (!document.getElementById(`ringado-style-${className}`)) {
      const styleElement = document.createElement('style');
      styleElement.id = `ringado-style-${className}`;
      styleElement.textContent = `
        .${className} {
          height: ${optimalHeight}px !important;
          max-height: ${optimalHeight}px !important;
        }
      `;
      document.head.appendChild(styleElement);
    }

    logger.info('Widget height adjusted for device', {
      viewport,
      deviceInfo,
      optimalHeight,
      className,
    });
  }

  /**
   * Détecte des appareils spécifiques pour des optimisations ciblées
   */
  detectSpecificDevice(viewport) {
    const userAgent = navigator.userAgent;

    return {
      isIOS: /iPad|iPhone|iPod/.test(userAgent),
      isAndroid: /Android/.test(userAgent),
      isSafari: /Safari/.test(userAgent) && !/Chrome/.test(userAgent),
      isIPhoneLandscape:
        /iPhone/.test(userAgent) &&
        viewport.isLandscape &&
        viewport.height <= 430,
      isIPad:
        /iPad/.test(userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1),
      hasNotch:
        viewport.width === 390 ||
        viewport.width === 428 ||
        viewport.width === 375, // iPhone X/11/12/13/14 series
      isSmallScreen: viewport.width <= 360 && viewport.height <= 640,
    };
  }

  /**
   * Détermine si l'appareil est un mobile
   */
  isMobileDevice() {
    return (
      window.innerWidth <= 768 ||
      'ontouchstart' in window ||
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      )
    );
  }

  createWidget() {
    // Main widget container
    this.elements.widget = Utils.createElement(
      'div',
      `ringado-widget ${this.config.position}`
    );

    // Chat button
    this.createChatButton();

    // Chat modal
    this.createChatModal();

    // Append to body
    document.body.appendChild(this.elements.widget);

    // Initially hide widget
    this.elements.widget.style.opacity = '0';
    this.elements.widget.style.transform = 'scale(0.75) translateY(16px)';
    this.elements.widget.style.transition = 'all 0.3s ease';
  }

  createChatButton() {
    const button = Utils.createElement('div', 'ringado-chat-button');

    // Phosphor hand-waving icon (simplified)
    const handIcon = Utils.createElement('div', 'chat-icon');
    handIcon.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 256 256">
        <path d="M220.17,100,202.86,70a28,28,0,0,0-38.24-10.25,27.69,27.69,0,0,0-9,8.34L138.2,38a28,28,0,0,0-48.48,0A28,28,0,0,0,48.15,74l1.59,2.76A27.67,27.67,0,0,0,38,80.41a28,28,0,0,0-10.24,38.25l40,69.32a87.47,87.47,0,0,0,53.43,41,88.56,88.56,0,0,0,22.92,3,88,88,0,0,0,76.06-132Zm-6.66,62.64A72,72,0,0,1,81.62,180l-40-69.32a12,12,0,0,1,20.78-12L81.63,132a8,8,0,1,0,13.85-8L62,66A12,12,0,1,1,82.78,54L114,108a8,8,0,1,0,13.85-8L103.57,58h0a12,12,0,1,1,20.78-12l33.42,57.9a48,48,0,0,0-5.54,60.6,8,8,0,0,0,13.24-9A32,32,0,0,1,172.78,112a8,8,0,0,0,2.13-10.4L168.23,90A12,12,0,1,1,189,78l17.31,30A71.56,71.56,0,0,1,213.51,162.62ZM184.25,31.71A8,8,0,0,1,194,26a59.62,59.62,0,0,1,36.53,28l.33.57a8,8,0,1,1-13.85,8l-.33-.57a43.67,43.67,0,0,0-26.8-20.5A8,8,0,0,1,184.25,31.71ZM80.89,237a8,8,0,0,1-11.23,1.33A119.56,119.56,0,0,1,40.06,204a8,8,0,0,1,13.86-8,103.67,103.67,0,0,0,25.64,29.72A8,8,0,0,1,80.89,237Z"/>
      </svg>
    `;

    button.appendChild(handIcon);

    // Click event
    button.addEventListener('click', () => {
      this.openChat();
    });

    this.elements.button = button;
    this.elements.widget.appendChild(button);
  }

  createChatModal() {
    // Modal container (no overlay)
    const modal = Utils.createElement('div', 'ringado-chat-modal');

    // Chat container
    const container = Utils.createElement('div', 'ringado-chat-container');

    // Header
    this.createHeader(container);

    // Messages area
    this.createMessagesArea(container);

    // Input area
    this.createInputArea(container);

    // Powered by footer (only if not pro)
    const isPro = this.isUserPro();
    logger.info('createChatModal: checking if user is Pro for footer display', {
      isPro,
      userId: this.config.userId,
      targetOperatorId: this.config.targetOperatorId,
      detectedProStatus: this.state.detectedProStatus,
      configIsPro: this.config.isPro,
    });

    if (!isPro) {
      logger.info('createChatModal: creating footer (user is not Pro)');
      this.createFooter(container);
    } else {
      logger.info('createChatModal: skipping footer creation (user is Pro)');
    }

    modal.appendChild(container);

    // Pas d'événement de clic pour fermer sur l'overlay car plus d'overlay
    // Seul le bouton X permet de fermer

    this.elements.modal = modal;
    this.elements.container = container;

    // IMPORTANT : Ajouter la modal directement au body, pas au widget
    // pour éviter l'héritage du positionnement du widget
    document.body.appendChild(modal);
  }

  createHeader(container) {
    const header = Utils.createElement('div', 'ringado-chat-header');

    // Status info
    const statusInfo = Utils.createElement('div', 'status-info');

    // Status dot
    const statusDot = Utils.createElement(
      'div',
      'ringado-status-dot assistant'
    );

    // Status text container
    const statusTextContainer = Utils.createElement(
      'div',
      'status-text-container'
    );

    // Status text
    const statusText = Utils.createElement('span', 'status-text');
    statusText.textContent = this.getDisplayName();

    statusTextContainer.appendChild(statusText);

    // Live badge (initially hidden)
    const liveBadge = Utils.createElement('span', 'ringado-live-badge');
    liveBadge.textContent = 'live';
    liveBadge.style.display = 'none';
    statusTextContainer.appendChild(liveBadge);

    statusInfo.appendChild(statusDot);
    statusInfo.appendChild(statusTextContainer);

    // Close button
    const closeButton = Utils.createElement('button', 'ringado-close-button');
    closeButton.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
      </svg>
    `;

    closeButton.addEventListener('click', () => {
      this.closeChat();
    });

    header.appendChild(statusInfo);
    header.appendChild(closeButton);

    this.elements.header = header;
    this.elements.statusDot = statusDot;
    this.elements.statusText = statusText;
    this.elements.liveBadge = liveBadge;

    container.appendChild(header);
  }

  createMessagesArea(container) {
    const messagesArea = Utils.createElement('div', 'ringado-messages');

    this.elements.messages = messagesArea;
    container.appendChild(messagesArea);

    // Add welcome message
    this.addWelcomeMessage();
  }

  createInputArea(container) {
    const inputArea = Utils.createElement('div', 'ringado-input-area');

    // Input container
    const inputContainer = Utils.createElement(
      'div',
      'ringado-input-container'
    );

    // Input field
    const input = Utils.createElement('input', 'ringado-input');
    input.type = 'text';

    // Send button
    const sendButton = Utils.createElement('button', 'ringado-send-button');
    sendButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 256 256" style="fill: white !important;">
        <path d="M231.87,114l-168-95.89A16,16,0,0,0,40.92,37.34L71.55,128,40.92,218.67A16,16,0,0,0,56,240a16.15,16.15,0,0,0,7.93-2.1l167.92-96.05a16,16,0,0,0,.05-27.89ZM56,224a.56.56,0,0,0,0-.12L85.74,136H144a8,8,0,0,0,0-16H85.74L56.06,32.16A.46.46,0,0,0,56,32l168,95.83Z"></path>
      </svg>
    `;

    inputContainer.appendChild(input);
    inputContainer.appendChild(sendButton);

    // Email decline button (shown when asking for email)
    const emailDecline = Utils.createElement('div', 'ringado-decline-email');
    emailDecline.style.display = 'none';
    const emailDeclineButton = Utils.createElement('button');
    emailDeclineButton.textContent = 'Skip';
    emailDecline.appendChild(emailDeclineButton);

    inputArea.appendChild(inputContainer);
    inputArea.appendChild(emailDecline);

    // Event listeners
    input.addEventListener('keyup', (e) => {
      if (e.key === 'Enter') {
        this.sendMessage();
      }
    });

    sendButton.addEventListener('click', () => {
      this.sendMessage();
    });

    emailDeclineButton.addEventListener('click', () => {
      this.declineEmail();
    });

    // Update send button state
    input.addEventListener('input', () => {
      sendButton.disabled = !input.value.trim();
    });

    this.elements.input = input;
    this.elements.sendButton = sendButton;
    this.elements.emailDecline = emailDecline;
    this.elements.inputArea = inputArea;

    // Update input placeholder now that elements are defined
    this.updateInputPlaceholder();

    container.appendChild(inputArea);
  }

  createFooter(container) {
    const footer = Utils.createElement('div', 'ringado-powered-by');

    const link = Utils.createElement('a');
    link.href = 'https://ringado.com';
    link.target = '_blank';
    link.innerHTML = `
      <span>Powered by</span>
      <div class="logo">R</div>
      <span>Ringado</span>
    `;

    footer.appendChild(link);
    container.appendChild(footer);

    // Stocker la référence pour pouvoir le supprimer plus tard
    this.elements.footer = footer;
  }

  /**
   * Met à jour l'affichage du footer selon le statut Pro
   */
  updateFooterVisibility() {
    const isPro = this.isUserPro();
    logger.info('updateFooterVisibility: checking Pro status', {
      isPro,
      footerExists: !!this.elements.footer,
      detectedProStatus: this.state.detectedProStatus,
      userId: this.config.userId,
      targetOperatorId: this.config.targetOperatorId,
    });

    if (isPro && this.elements.footer && this.elements.footer.parentNode) {
      logger.info('updateFooterVisibility: removing footer (user is Pro)');
      this.elements.footer.parentNode.removeChild(this.elements.footer);
      this.elements.footer = null;
    } else if (!isPro && !this.elements.footer) {
      // If user is not Pro and footer doesn't exist, create it
      logger.info('updateFooterVisibility: creating footer (user is not Pro)');
      const container = document.querySelector('.ringado-chat-container');
      if (container) {
        this.createFooter(container);
      }
    }
  }

  // Widget visibility methods
  showWidget() {
    this.state.isWidgetVisible = true;
    this.elements.widget.style.opacity = '1';
    this.elements.widget.style.transform = 'scale(1) translateY(0)';
  }

  hideWidget() {
    this.state.isWidgetVisible = false;
    this.elements.widget.style.opacity = '0';
    this.elements.widget.style.transform = 'scale(0.75) translateY(16px)';
  }

  // Chat methods
  openChat() {
    logger.info('Opening chat - current state:', {
      isOpen: this.state.isOpen,
      isWidgetVisible: this.state.isWidgetVisible,
      modal: !!this.elements.modal,
      widget: !!this.elements.widget,
    });

    this.state.isOpen = true;

    if (this.elements.modal) {
      this.elements.modal.classList.add('open');
      logger.info('Added "open" class to modal');
    } else {
      logger.error('Modal element not found!');
    }

    if (this.elements.widget) {
      this.elements.widget.classList.add('modal-open');
      logger.info('Added "modal-open" class to widget');
    } else {
      logger.error('Widget element not found!');
    }

    // Ajuster la hauteur pour l'appareil lors de l'ouverture
    setTimeout(() => {
      this.adjustWidgetForDevice();
    }, 50);

    // Focus input
    setTimeout(() => {
      if (this.elements.input) {
        this.elements.input.focus();
        logger.info('Input focused');
      } else {
        logger.error('Input element not found!');
      }
    }, 300);

    // Widget messaging
    if (WidgetMessaging.isWidgetMode()) {
      WidgetMessaging.signalChatOpened();
    }

    // Callback
    if (this.config.callbacks.onChatOpen) {
      this.config.callbacks.onChatOpen(this);
    }
  }

  closeChat() {
    logger.info('Closing chat');

    this.state.isOpen = false;
    this.elements.modal.classList.remove('open');
    this.elements.widget.classList.remove('modal-open');

    // Show widget again with delay plus long pour synchroniser avec l'animation plus smooth
    setTimeout(() => {
      if (!this.state.isOpen) {
        this.showWidget();
      }
    }, 500); // Délai augmenté pour correspondre à la nouvelle transition de 0.5s

    // Widget messaging
    if (WidgetMessaging.isWidgetMode()) {
      WidgetMessaging.signalChatClosed();
    }

    // Callback
    if (this.config.callbacks.onChatClose) {
      this.config.callbacks.onChatClose(this);
    }
  }

  // Message methods
  addWelcomeMessage() {
    const welcomeText = this.getWelcomeMessage();
    this.addMessage('assistant', welcomeText);
  }

  getWelcomeMessage() {
    // Toujours demander le nom en premier, très poliment en anglais
    if (this.state.conversationStep === 0) {
      return 'Hello! May I please have your name?';
    }
    return 'Hi! How can I help you today?';
  }

  addMessage(type, content, timestamp = new Date()) {
    const messageContainer = Utils.createElement(
      'div',
      `ringado-message ${type}`
    );

    if (type !== 'user') {
      // Avatar
      const avatar = Utils.createElement(
        'div',
        `ringado-avatar ${
          this.state.isConnectedToOperator ? 'connected' : 'assistant'
        }`
      );

      if (this.state.isConnectedToOperator && this.state.operatorInfo?.avatar) {
        const img = Utils.createElement('img');
        img.src = this.state.operatorInfo.avatar;
        img.alt = this.state.operatorInfo.name || 'Operator';
        avatar.appendChild(img);
      } else {
        const icon = Utils.createElement('div', 'icon');
        // Logique d'affichage des avatars :
        // - Phase de collecte d'infos (nom/email) : robot
        // - Chat normal mais opérateur pas connecté : robot
        // - Opérateur connecté : avatar humain
        const isInCollectionPhase = this.state.conversationStep < 2;
        const shouldShowRobotAvatar =
          isInCollectionPhase || !this.state.isConnectedToOperator;

        icon.innerHTML = shouldShowRobotAvatar
          ? '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256"><path d="M200,48H136V16a8,8,0,0,0-16,0V48H56A32,32,0,0,0,24,80V192a32,32,0,0,0,32,32H200a32,32,0,0,0,32-32V80A32,32,0,0,0,200,48Zm16,144a16,16,0,0,1-16,16H56a16,16,0,0,1-16-16V80A16,16,0,0,1,56,64H200a16,16,0,0,1,16,16Zm-52-56H92a28,28,0,0,0,0,56h72a28,28,0,0,0,0-56Zm-24,16v24H116V152ZM80,164a12,12,0,0,1,12-12h8v24H92A12,12,0,0,1,80,164Zm84,12h-8V152h8a12,12,0,0,1,0,24ZM72,108a12,12,0,1,1,12,12A12,12,0,0,1,72,108Zm88,0a12,12,0,1,1,12,12A12,12,0,0,1,160,108Z"></path></svg>'
          : '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256"><path d="M230.92,212c-15.23-26.33-38.7-45.21-66.09-54.16a72,72,0,1,0-73.66,0C63.78,166.78,40.31,185.66,25.08,212a8,8,0,1,0,13.85,8c18.84-32.56,52.14-52,89.07-52s70.23,19.44,89.07,52a8,8,0,1,0,13.85-8ZM72,96a56,56,0,1,1,56,56A56.06,56.06,0,0,1,72,96Z"></path></svg>';
        avatar.appendChild(icon);
      }

      messageContainer.appendChild(avatar);
    }

    // Message bubble
    const bubble = Utils.createElement('div', `ringado-message-bubble`);
    bubble.innerHTML = content;

    messageContainer.appendChild(bubble);

    this.elements.messages.appendChild(messageContainer);
    this.scrollToBottom();

    // Store message
    this.state.messages.push({
      type,
      content,
      timestamp,
    });
  }

  showTypingIndicator() {
    if (this.elements.typingIndicator) return;

    const typing = Utils.createElement('div', 'ringado-typing');

    // Avatar
    const avatar = Utils.createElement(
      'div',
      `ringado-avatar ${
        this.state.isConnectedToOperator ? 'connected' : 'assistant'
      }`
    );
    const icon = Utils.createElement('div', 'icon');
    // Même logique que pour les messages : robot pendant la collecte d'infos, humain quand opérateur connecté
    const isInCollectionPhase = this.state.conversationStep < 2;
    const shouldShowRobotAvatar =
      isInCollectionPhase || !this.state.isConnectedToOperator;

    icon.innerHTML = shouldShowRobotAvatar
      ? '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256"><path d="M200,48H136V16a8,8,0,0,0-16,0V48H56A32,32,0,0,0,24,80V192a32,32,0,0,0,32,32H200a32,32,0,0,0,32-32V80A32,32,0,0,0,200,48Zm16,144a16,16,0,0,1-16,16H56a16,16,0,0,1-16-16V80A16,16,0,0,1,56,64H200a16,16,0,0,1,16,16Zm-52-56H92a28,28,0,0,0,0,56h72a28,28,0,0,0,0-56Zm-24,16v24H116V152ZM80,164a12,12,0,0,1,12-12h8v24H92A12,12,0,0,1,80,164Zm84,12h-8V152h8a12,12,0,0,1,0,24ZM72,108a12,12,0,1,1,12,12A12,12,0,0,1,72,108Zm88,0a12,12,0,1,1,12,12A12,12,0,0,1,160,108Z"></path></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256"><path d="M230.92,212c-15.23-26.33-38.7-45.21-66.09-54.16a72,72,0,1,0-73.66,0C63.78,166.78,40.31,185.66,25.08,212a8,8,0,1,0,13.85,8c18.84-32.56,52.14-52,89.07-52s70.23,19.44,89.07,52a8,8,0,1,0,13.85-8ZM72,96a56,56,0,1,1,56,56A56.06,56.06,0,0,1,72,96Z"></path></svg>';
    avatar.appendChild(icon);

    // Typing dots
    const bubble = Utils.createElement('div', 'ringado-message-bubble');
    for (let i = 0; i < 3; i++) {
      const dot = Utils.createElement('div', 'ringado-typing-dot');
      bubble.appendChild(dot);
    }

    typing.appendChild(avatar);
    typing.appendChild(bubble);

    this.elements.typingIndicator = typing;
    this.elements.messages.appendChild(typing);
    this.scrollToBottom();
  }

  hideTypingIndicator() {
    if (this.elements.typingIndicator) {
      this.elements.typingIndicator.remove();
      this.elements.typingIndicator = null;
    }
  }

  async sendMessage() {
    const message = this.elements.input.value.trim();
    if (!message) return;

    // Clear input
    this.elements.input.value = '';
    this.elements.sendButton.disabled = true;

    // Add user message to UI immediately
    this.addMessage('user', message);

    try {
      // Handle conversation flow
      await this.handleConversationFlow(message);
    } catch (error) {
      logger.error('Error sending message:', error);
      this.addMessage(
        'assistant',
        'Sorry, something went wrong. Please try again.'
      );
    } finally {
      this.elements.sendButton.disabled = false;
    }

    // Callback
    if (this.config.callbacks.onMessageSent) {
      this.config.callbacks.onMessageSent(message, this);
    }
  }

  async handleConversationFlow(message) {
    // Toujours collecter le nom en premier
    if (this.state.conversationStep === 0) {
      // Collect name
      this.state.visitorName = message;

      // Si l'opérateur est Pro, demander l'email (avec possibilité de refuser)
      if (this.isUserPro()) {
        this.state.conversationStep = 1;

        setTimeout(() => {
          this.addMessage(
            'assistant',
            `Thank you, ${this.state.visitorName}! To provide you with the best possible assistance, may I please have your email address? (You're welcome to decline if you prefer.)`
          );
          this.updateInputPlaceholder();
          this.showEmailDeclineOption();
        }, 800);
      } else {
        // Si l'opérateur n'est pas Pro, passer directement au chat
        this.state.conversationStep = 2;

        setTimeout(async () => {
          this.addMessage(
            'assistant',
            `Thank you, ${this.state.visitorName}! How may I assist you today?`
          );
          this.updateInputPlaceholder();

          // Créer la conversation maintenant que nous avons le nom
          await this.createConversationIfNeeded();
        }, 800);
      }
    } else if (this.state.conversationStep === 1) {
      // Collect email (seulement pour les opérateurs Pro)
      if (this.isValidEmail(message)) {
        this.state.visitorEmail = message;
        this.state.isEmailCollected = true;
        this.state.conversationStep = 2;

        setTimeout(async () => {
          this.addMessage(
            'assistant',
            `Perfect! Thank you for providing your email. How can I help you today?`
          );
          this.updateInputPlaceholder();
          this.hideEmailDeclineOption();

          // Créer la conversation maintenant que nous avons les informations
          await this.createConversationIfNeeded();
        }, 800);
      } else {
        setTimeout(() => {
          this.addMessage(
            'assistant',
            `Please enter a valid email address, or feel free to decline if you prefer not to share it.`
          );
        }, 800);
      }
    } else {
      // Normal chat flow - créer la conversation si nécessaire puis envoyer le message
      await this.createConversationIfNeeded();
      await this.sendMessageToAPI(message);
    }
  }

  /**
   * Créer une conversation si elle n'existe pas déjà
   */
  async createConversationIfNeeded() {
    if (this.state.currentConversationId) {
      return; // Conversation déjà créée
    }

    if (!this.config.operatorId) {
      const error = new Error(
        'No operator ID configured. Please set data-operator-id attribute or configure window.RingadoConfig.operatorId'
      );
      logger.error('Configuration error:', error);
      throw error;
    }

    // S'assurer qu'on a un nom de visiteur
    if (!this.state.visitorName) {
      this.state.visitorName = 'Anonymous Visitor';
    }

    const visitorName = this.state.visitorName;

    try {
      logger.info('Creating new conversation', {
        visitorName,
        operatorId: this.config.operatorId,
        hasEmail: !!this.state.visitorEmail,
      });

      const visitorData = {
        name: visitorName,
        email: this.state.visitorEmail,
        userId: this.config.userId,
      };

      const conversationData = await this.api.createConversation(
        visitorData,
        this.config.operatorId
      );

      this.state.currentConversationId = conversationData.conversation.id;
      this.state.operatorInfo = conversationData.operator;

      // Récupérer et stocker l'information du plan client depuis la réponse de création
      if (conversationData.client) {
        const isClientPro = conversationData.client.planType === 'pro';
        const clientName = conversationData.client.name;

        // Mettre à jour le statut Pro détecté si on n'en avait pas déjà un
        if (this.state.detectedProStatus === null) {
          this.state.detectedProStatus = isClientPro;
          this.state.detectedClientName = clientName;

          logger.info('Client plan info retrieved from conversation creation', {
            planType: conversationData.client.planType,
            isPro: isClientPro,
            clientName: clientName,
          });

          // Mettre à jour l'affichage du footer selon le nouveau statut
          this.updateFooterVisibility();
        }
      }

      logger.info('Conversation created successfully', {
        conversationId: this.state.currentConversationId,
        operatorEmail: conversationData.operator.email,
      });

      // Démarrer le polling pour les nouveaux messages
      this.startMessagePolling();

      // Vérifier le statut de l'opérateur
      this.checkOperatorStatus();
    } catch (error) {
      logger.error('Failed to create conversation:', error);

      // Fournir des messages d'erreur plus explicites
      if (error.message.includes('No operator ID')) {
        const detailedError = new Error(
          'Widget configuration error: No operator ID provided. Please add data-operator-id="YOUR_ID" to the script tag or configure window.RingadoConfig.operatorId'
        );
        throw detailedError;
      }

      if (error.message.includes('HTTP 4')) {
        const detailedError = new Error(
          'API error: Invalid operator ID or missing permissions. Please verify your operator ID is correct and active.'
        );
        throw detailedError;
      }

      if (
        error.message.includes('Failed to fetch') ||
        error.message.includes('NetworkError')
      ) {
        const detailedError = new Error(
          'Network error: Unable to connect to Ringado API. Please check your internet connection and ensure the API is accessible.'
        );
        throw detailedError;
      }

      throw error;
    }
  }

  /**
   * Envoyer un message à l'API
   */
  async sendMessageToAPI(content) {
    if (!this.state.currentConversationId) {
      throw new Error('No active conversation');
    }

    const senderData = {
      type: 'visitor',
      name: this.state.visitorName || 'Anonymous Visitor',
      userId: this.config.userId,
    };

    try {
      await this.api.sendMessage(
        this.state.currentConversationId,
        content,
        senderData
      );

      logger.info('Message sent successfully', {
        conversationId: this.state.currentConversationId,
        contentLength: content.length,
      });
    } catch (error) {
      logger.error('Failed to send message:', error);
      throw error;
    }
  }

  /**
   * Démarrer le polling pour récupérer les nouveaux messages
   */
  startMessagePolling() {
    // Arrêter le polling existant s'il existe
    this.stopMessagePolling();

    this.messagePollingInterval = setInterval(async () => {
      try {
        await this.fetchNewMessages();
      } catch (error) {
        logger.error('Error during message polling:', error);
      }
    }, 2000); // Poll toutes les 2 secondes

    this.intervals.push(this.messagePollingInterval);
  }

  /**
   * Arrêter le polling des messages
   */
  stopMessagePolling() {
    if (this.messagePollingInterval) {
      clearInterval(this.messagePollingInterval);
      this.messagePollingInterval = null;
    }
  }

  /**
   * Récupérer les nouveaux messages
   */
  async fetchNewMessages() {
    if (!this.state.currentConversationId) {
      return;
    }

    try {
      const messageData = await this.api.getMessages(
        this.state.currentConversationId,
        this.state.lastMessageId
      );

      if (messageData.hasNewMessages && messageData.messages.length > 0) {
        // Ajouter les nouveaux messages à l'interface
        for (const message of messageData.messages) {
          if (message.senderType === 'operator') {
            this.addMessage('assistant', message.content);

            // Callback pour message reçu
            if (this.config.callbacks.onMessageReceived) {
              this.config.callbacks.onMessageReceived(message.content, this);
            }
          }

          // Mettre à jour le dernier message ID
          this.state.lastMessageId = message.id;
        }
      }
    } catch (error) {
      logger.error('Failed to fetch new messages:', error);
    }
  }

  /**
   * Vérifier le statut de l'opérateur
   */
  async checkOperatorStatus() {
    if (!this.state.currentConversationId) {
      return;
    }

    try {
      const statusData = await this.api.getConversationStatus(
        this.state.currentConversationId
      );

      const wasOperatorOnline = this.state.isConnectedToOperator;
      this.state.isConnectedToOperator = statusData.operator.isOnline;
      this.state.operatorInfo = statusData.operator.info;

      // Récupérer et stocker l'information du plan client depuis l'endpoint /api/widget/status
      if (statusData.client) {
        const isClientPro = statusData.client.planType === 'pro';
        const clientName = statusData.client.name;

        // Mettre à jour le statut Pro détecté si on n'en avait pas déjà un
        if (this.state.detectedProStatus === null) {
          this.state.detectedProStatus = isClientPro;
          this.state.detectedClientName = clientName;

          logger.info('Client plan info retrieved from conversation status', {
            planType: statusData.client.planType,
            isPro: isClientPro,
            clientName: clientName,
          });

          // Mettre à jour l'affichage du footer selon le nouveau statut
          this.updateFooterVisibility();
        }
      }

      // Notifier si l'opérateur vient de se connecter
      if (!wasOperatorOnline && this.state.isConnectedToOperator) {
        this.addMessage(
          'assistant',
          `${
            this.state.operatorInfo?.name || 'An operator'
          } is now online and will assist you.`
        );

        // Mettre à jour le header avec le nom de l'opérateur et le badge live
        this.updateHeaderForOperatorConnection();
      }

      logger.info('Operator status checked', {
        isOnline: this.state.isConnectedToOperator,
        operatorName: this.state.operatorInfo?.name,
      });
    } catch (error) {
      logger.error('Failed to check operator status:', error);
    }
  }

  simulateResponse(userMessage) {
    this.showTypingIndicator();

    setTimeout(() => {
      this.hideTypingIndicator();

      let response = 'I understand your question. Let me help you with that.';

      // Simple response logic (replace with your API call)
      if (
        userMessage.toLowerCase().includes('hello') ||
        userMessage.toLowerCase().includes('hi')
      ) {
        response = "Hello! I'm here to help. What can I do for you?";
      } else if (
        userMessage.toLowerCase().includes('price') ||
        userMessage.toLowerCase().includes('cost')
      ) {
        response =
          "I'd be happy to discuss pricing with you. Let me connect you with our team.";
      } else if (userMessage.toLowerCase().includes('help')) {
        response =
          "Of course! I'm here to help. What specific information are you looking for?";
      } else if (userMessage.includes('?')) {
        response =
          "That's a great question! Let me provide you with a detailed answer.";
      }

      this.addMessage('assistant', response);

      // Callback
      if (this.config.callbacks.onMessageReceived) {
        this.config.callbacks.onMessageReceived(response, this);
      }
    }, 800 + Math.random() * 1200);
  }

  async declineEmail() {
    this.state.isEmailCollected = true;
    this.state.conversationStep = 2;

    this.addMessage('assistant', 'No problem! How can I help you today?');
    this.updateInputPlaceholder();
    this.hideEmailDeclineOption();

    // Créer la conversation maintenant que l'utilisateur a décliné l'email
    try {
      await this.createConversationIfNeeded();
    } catch (error) {
      logger.error('Failed to create conversation after email decline:', error);
    }
  }

  /**
   * Met à jour le header quand l'opérateur se connecte
   */
  updateHeaderForOperatorConnection() {
    if (this.elements.statusText) {
      this.elements.statusText.textContent = this.getDisplayName();
    }

    if (this.elements.statusDot) {
      this.elements.statusDot.className = 'ringado-status-dot connected';
    }

    if (this.elements.liveBadge) {
      this.elements.liveBadge.style.display = 'inline-block';
    }
  }

  // Utility methods
  updateInputPlaceholder() {
    const placeholders = {
      0: 'Your name...',
      1: 'Your email...',
      2: 'Your message...',
    };

    this.elements.input.placeholder =
      placeholders[this.state.conversationStep] || 'Your message...';
  }

  showEmailDeclineOption() {
    if (this.state.conversationStep === 1) {
      this.elements.emailDecline.style.display = 'block';
    }
  }

  hideEmailDeclineOption() {
    this.elements.emailDecline.style.display = 'none';
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  scrollToBottom() {
    setTimeout(() => {
      this.elements.messages.scrollTop = this.elements.messages.scrollHeight;
    }, 100);
  }

  // Public API methods
  show() {
    this.showWidget();
  }

  hide() {
    this.hideWidget();
  }

  toggle() {
    if (this.state.isOpen) {
      this.closeChat();
    } else {
      this.openChat();
    }
  }

  destroy() {
    // Stop message polling
    this.stopMessagePolling();

    // Clear intervals and timeouts
    this.intervals.forEach(clearInterval);
    this.timeouts.forEach(clearTimeout);

    // Remove responsive event listeners
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      window.removeEventListener('orientationchange', this.resizeHandler);
    }

    // Nettoyer le gestionnaire du clavier virtuel
    if (this.virtualKeyboardHandler) {
      this.virtualKeyboardHandler.destroy();
      this.virtualKeyboardHandler = null;
    }

    // Remove dynamic height styles
    document
      .querySelectorAll('[id^="ringado-style-ringado-dynamic-height-"]')
      .forEach((el) => {
        el.remove();
      });

    // Remove keyboard adjustment styles
    const keyboardStyles = document.getElementById('ringado-keyboard-styles');
    if (keyboardStyles) {
      keyboardStyles.remove();
    }

    // Close conversation if active
    if (this.state.currentConversationId) {
      this.api
        .closeConversation(this.state.currentConversationId, 'Widget destroyed')
        .catch((error) =>
          logger.error('Failed to close conversation on destroy:', error)
        );
    }

    // Remove modal from body if it exists
    if (this.elements.modal && this.elements.modal.parentNode) {
      this.elements.modal.remove();
    }

    // Remove widget from DOM
    if (this.elements.widget) {
      this.elements.widget.remove();
    }

    // Remove styles if no other widgets
    const styleElement = document.getElementById('ringado-widget-styles');
    if (styleElement) {
      styleElement.remove();
    }

    logger.info('Widget destroyed');
  }

  /**
   * Détecte les informations du client (statut Pro, nom) si userId est fourni
   */
  async detectClientInfoIfNeeded() {
    // Prioriser userId, mais utiliser targetOperatorId si userId n'est pas défini
    const idToDetect = this.config.userId || this.config.targetOperatorId;

    if (!idToDetect) {
      logger.info(
        'detectClientInfoIfNeeded: No userId or targetOperatorId, skipping detection'
      );
      return; // Aucun ID, pas de détection
    }

    if (this.state.isLoadingClientInfo) {
      logger.info('detectClientInfoIfNeeded: Already loading, skipping');
      return; // Éviter les requêtes multiples
    }

    try {
      this.state.isLoadingClientInfo = true;
      logger.info('detectClientInfoIfNeeded: Starting client info detection', {
        idToDetect,
        isUserId: !!this.config.userId,
        isTargetOperatorId: !!this.config.targetOperatorId,
      });

      let clientInfo;
      if (this.config.userId) {
        // Utiliser la nouvelle API users pour les userIds
        clientInfo = await this.clientPlanDetector.detectUserStatus(
          this.config.userId
        );
        logger.info('detectClientInfoIfNeeded: Using user API for userId', {
          userId: this.config.userId,
        });
      } else {
        // Utiliser l'ancienne API clients pour les targetOperatorIds (compatibilité)
        clientInfo = await this.clientPlanDetector.detectClientStatus(
          this.config.targetOperatorId
        );
        logger.info(
          'detectClientInfoIfNeeded: Using client API for targetOperatorId',
          {
            targetOperatorId: this.config.targetOperatorId,
          }
        );
      }

      this.state.detectedProStatus = clientInfo.isPro;
      this.state.detectedClientName = clientInfo.name;

      logger.info('detectClientInfoIfNeeded: Client info detected', {
        id: idToDetect,
        isPro: clientInfo.isPro,
        name: clientInfo.name,
        plan: clientInfo.plan,
        context: this.config.userId ? 'user' : 'targetOperator',
      });

      logger.info('detectClientInfoIfNeeded: State updated', {
        detectedProStatus: this.state.detectedProStatus,
        detectedClientName: this.state.detectedClientName,
      });

      // Mettre à jour l'affichage du footer selon le nouveau statut
      this.updateFooterVisibility();
    } catch (error) {
      logger.error(
        'detectClientInfoIfNeeded: Failed to detect client info',
        error
      );
      // En cas d'erreur, considérer comme non-pro (par sécurité)
      this.state.detectedProStatus = false;
      this.state.detectedClientName = null;
    } finally {
      this.state.isLoadingClientInfo = false;
      logger.info('detectClientInfoIfNeeded: Detection completed', {
        finalProStatus: this.state.detectedProStatus,
        finalClientName: this.state.detectedClientName,
      });
    }
  }

  /**
   * Détermine si l'utilisateur est Pro
   * @returns {boolean}
   */
  isUserPro() {
    // Si isPro est explicitement défini dans la config, l'utiliser
    if (this.config.isPro !== undefined) {
      logger.info('isUserPro: using explicit isPro config', this.config.isPro);
      return this.config.isPro;
    }

    // Si on a un userId, utiliser le statut détecté automatiquement
    if (this.config.userId) {
      const result = this.state.detectedProStatus === true;
      logger.info('isUserPro: using detected status for userId', {
        userId: this.config.userId,
        detectedProStatus: this.state.detectedProStatus,
        result,
      });
      return result;
    }

    // Si on a un targetOperatorId (contexte widget sur page /u/[id]), utiliser le statut de l'opérateur cible
    if (this.config.targetOperatorId) {
      const result = this.state.detectedProStatus === true;
      logger.info('isUserPro: using detected status for targetOperatorId', {
        targetOperatorId: this.config.targetOperatorId,
        detectedProStatus: this.state.detectedProStatus,
        result,
      });
      return result;
    }

    // Si on a détecté un statut Pro depuis l'endpoint /api/widget/status (même sans userId/targetOperatorId)
    if (this.state.detectedProStatus !== null) {
      const result = this.state.detectedProStatus === true;
      logger.info('isUserPro: using detected status from conversation', {
        detectedProStatus: this.state.detectedProStatus,
        result,
      });
      return result;
    }

    // Par défaut, considérer comme non-pro
    logger.info('isUserPro: default to false (no userId or targetOperatorId)');
    return false;
  }

  /**
   * Détermine si l'email doit être requis
   * @returns {boolean}
   */
  shouldRequireEmail() {
    // Si requireEmail est explicitement défini dans la config, l'utiliser
    if (this.config.requireEmail !== undefined) {
      logger.info(
        'shouldRequireEmail: using explicit requireEmail config',
        this.config.requireEmail
      );
      return this.config.requireEmail;
    }

    // Si isPro est explicitement défini dans la config, l'utiliser (inversé: pro = pas d'email requis)
    if (this.config.isPro !== undefined) {
      const result = !this.config.isPro; // ✅ FIXÉ: si pro = true, alors email requis = false
      logger.info('shouldRequireEmail: using explicit isPro config', {
        isPro: this.config.isPro,
        emailRequired: result,
      });
      return result;
    }

    // Si on a un userId, utiliser le statut détecté automatiquement (inversé)
    if (this.config.userId) {
      const result = this.state.detectedProStatus !== true; // ✅ FIXÉ: si pro = true, alors email requis = false
      logger.info('shouldRequireEmail: using detected status for userId', {
        userId: this.config.userId,
        detectedProStatus: this.state.detectedProStatus,
        emailRequired: result,
      });
      return result;
    }

    // Si on a un targetOperatorId (contexte widget sur page /u/[id]), utiliser le statut de l'opérateur cible (inversé)
    if (this.config.targetOperatorId) {
      const result = this.state.detectedProStatus !== true; // ✅ FIXÉ: si pro = true, alors email requis = false
      logger.info(
        'shouldRequireEmail: using detected status for targetOperatorId',
        {
          targetOperatorId: this.config.targetOperatorId,
          detectedProStatus: this.state.detectedProStatus,
          emailRequired: result,
        }
      );
      return result;
    }

    // Si on a détecté un statut Pro depuis l'endpoint /api/widget/status (même sans userId/targetOperatorId)
    if (this.state.detectedProStatus !== null) {
      const result = this.state.detectedProStatus !== true; // ✅ si pro = true, alors email requis = false
      logger.info(
        'shouldRequireEmail: using detected status from conversation',
        {
          detectedProStatus: this.state.detectedProStatus,
          emailRequired: result,
        }
      );
      return result;
    }

    // Par défaut, email requis (pour les utilisateurs non-pro)
    logger.info('shouldRequireEmail: default to true (non-pro)');
    return true;
  }

  /**
   * Retourne le nom du client détecté ou celui fourni dans la config
   * @returns {string|null}
   */
  getClientName() {
    // Prioriser le nom dans la config
    if (this.config.clientName) {
      return this.config.clientName;
    }

    // Utiliser le nom détecté
    return this.state.detectedClientName;
  }

  /**
   * Retourne le nom d'affichage approprié pour le header
   * @returns {string}
   */
  getDisplayName() {
    // Si connecté à un opérateur, afficher son nom
    if (this.state.isConnectedToOperator && this.state.operatorInfo?.name) {
      return this.state.operatorInfo.name;
    }

    // Sinon, afficher le nom du client ou "Assistant"
    return this.getClientName() || 'Assistant';
  }

  // Getters
  get isOpen() {
    return this.state.isOpen;
  }

  get isVisible() {
    return this.state.isWidgetVisible;
  }

  get messages() {
    return [...this.state.messages];
  }

  get visitorInfo() {
    return {
      name: this.state.visitorName,
      email: this.state.visitorEmail,
      isEmailCollected: this.state.isEmailCollected,
    };
  }

  get version() {
    return WIDGET_VERSION;
  }

  /**
   * Valide la configuration du widget
   */
  validateConfiguration() {
    const errors = [];
    const warnings = [];

    // Vérifier operatorId
    if (!this.config.operatorId) {
      errors.push(
        'operatorId is required. Please set data-operator-id attribute on the script tag or configure window.RingadoConfig.operatorId'
      );
    }

    // Vérifier baseUrl
    if (this.config.baseUrl && !this.config.baseUrl.startsWith('http')) {
      warnings.push(
        `Invalid baseUrl format: ${this.config.baseUrl}. Using default: https://ringado.com`
      );
    }

    // Log warnings
    warnings.forEach((warning) => {
      logger.warn('Configuration warning:', warning);
    });

    // Throw errors
    if (errors.length > 0) {
      const errorMessage =
        'Widget configuration errors:\n' +
        errors.map((e) => `- ${e}`).join('\n');
      logger.error('Configuration validation failed:', errorMessage);

      // Show user-friendly error in console
      console.error('🚨 Ringado Widget Configuration Error:');
      errors.forEach((error) => {
        console.error(`❌ ${error}`);
      });
      console.error(
        '\n📚 For help, see: https://docs.ringado.com/widget-integration'
      );

      throw new Error(errorMessage);
    }

    logger.info('✅ Widget configuration validated successfully');
  }

  /**
   * Détecte automatiquement la baseUrl selon l'environnement
   */
  detectBaseUrl() {
    // Vérifier d'abord si une baseUrl valide est déjà configurée
    const configuredBaseUrl = this.config.baseUrl;

    // Si une baseUrl est configurée et qu'elle semble valide (commence par http), l'utiliser
    if (
      configuredBaseUrl &&
      (configuredBaseUrl.startsWith('http://') ||
        configuredBaseUrl.startsWith('https://'))
    ) {
      logger.info('Using configured baseUrl', { baseUrl: configuredBaseUrl });
      return configuredBaseUrl;
    }

    // Toujours utiliser ringado.com comme baseUrl par défaut
    // Cela évite les erreurs 405 en développement local
    logger.info('Using default baseUrl: ringado.com');
    return 'https://ringado.com';
  }
}
